import React, { FC, useCallback, useContext, useDeferredValue, useMemo, useState } from 'react';

import { NativeSlickSlider, Slider } from '@src/atoms/Slider';
import { IGalleryFile } from './models';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { COEFF } from './const';
import { Unit } from '@src/core/Unit';
import { Box } from '@src/atoms/Box';
import { ImageRenderer } from '@src/atoms/ImageRenderer';
import { IClassNameProps, ILoadingProps, IStylesProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';
import { GallerySkeleton } from './_Skeleton/GallerySkeleton';
import { Zoom } from '@src/atoms/Zoom';
import { IMediaFile } from '@src/models/api';
import { IIdentify } from '@src/models/analytics';

import styled from './Gallery.scss';

export interface IGalleryProps extends IClassNameProps, IStylesProps, ILoadingProps, IIdentify {
  files: IGalleryFile[];
  roundCorners?: boolean;
}

/**
 * Галерея/слайдер картинок.
 *
 * Обязательно должна быть в Grid. Смотри примеры
 */
export const Gallery: FC<IGalleryProps> = ({ files = [], roundCorners = true, loading, className, style, id }) => {
  const [nav, setNav] = useState<NativeSlickSlider | null>(null);
  const [secondaryNav, setSecondaryNav] = useState<NativeSlickSlider | null>(null);
  const { isMobile } = useContext(BreakpointsContext);
  const [sliderHeight, setSliderHeight] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const unitEnv = useMemo(() => ({ gallery_id: id || 'empty' }), [id]);

  const onPreviewClick = (index: number) => {
    setActiveIndex(index);
    nav?.slickGoTo(index);
  };

  const beforeChange = (_: number, next: number) => {
    !isMobile && secondaryNav?.slickGoTo?.(next);
  };

  const handleChangeSliderHeight = useCallback(
    (h: number) => {
      !sliderHeight && setSliderHeight(h);
    },
    [sliderHeight],
  );

  const handleZoom = useCallback(() => {
    setOpen(!open);
  }, [open]);

  const hasManySlides = files.length > 1;

  const classes = classnames(className, styled.root, { [styled.rootMobile]: isMobile && hasManySlides });
  const classesBigContent = classnames(className, styled.sliderWrapper, styled.sliderBigContent, {
    [styled.sliderWrapperMobile]: isMobile,
  });
  const classesThumbs = classnames(className, styled.sliderWrapper, styled.sliderThumbnails);

  const rootItemClasses = classnames(styled.rootItem, {
    [styled.rootItem_roundCorners]: !!roundCorners,
  });

  const sliderHeightDeferred = useDeferredValue(sliderHeight);
  const mediaFiles = useMemo<IMediaFile[]>(
    () =>
      files.map((f) => ({
        id: f.id,
        fileSize: 0,
        mainUrl: f.url,
        originalName: f.originalName || f.name,
        previewUrl: f.thumb || f.url,
      })),
    [files],
  );

  const isOneHasDescription = files.some((f) => !!f.description);

  return (
    <Unit id={'Gallery'} env={unitEnv}>
      {loading || files.length === 0 ? (
        <Box className={classes} column justifyContent="spaceBetween">
          <GallerySkeleton className={rootItemClasses} />
        </Box>
      ) : (
        <Box className={styled.unitWrapper}>
          <div className={classes} style={style}>
            <div className={classesBigContent}>
              <Slider
                adaptiveHeight={!isOneHasDescription}
                fade
                swipe
                infinite
                arrows={!isMobile}
                dots={isMobile}
                ref={setNav}
                beforeChange={beforeChange}
                afterChange={(i) => setActiveIndex(i)}
              >
                {files.map((file) => (
                  <Slider.Item
                    key={file.id}
                    img={file}
                    height={sliderHeightDeferred}
                    changeHeight={handleChangeSliderHeight}
                    className={rootItemClasses}
                    coeff={COEFF}
                    handleZoom={handleZoom}
                    hasOneDescription={isOneHasDescription}
                  />
                ))}
              </Slider>
            </div>
            {!isMobile && files?.length > 1 && (
              <Unit id="galleryPreview">
                <div className={classesThumbs}>
                  <Slider
                    ref={setSecondaryNav}
                    slidesToShow={8}
                    slidesToScroll={1}
                    infinite={false}
                    dots={false}
                    swipe={false}
                    arrows={false}
                    responsive={[
                      {
                        breakpoint: 1440,
                        settings: {
                          slidesToShow: 6,
                        },
                      },
                      {
                        breakpoint: 1200,
                        settings: {
                          slidesToShow: 4,
                        },
                      },
                    ]}
                  >
                    {files.map((file, i) => (
                      <div key={file.id} className={styled.slideThumbWrapper}>
                        <Box
                          className={styled.slideThumb}
                          data-slide-id={file.id}
                          key={file.id}
                          onClick={() => onPreviewClick(i)}
                          data-id={`gallery_slideThumb_${file.id}`}
                        >
                          <ImageRenderer
                            url={file.thumb || file.url}
                            thumb={file.thumb}
                            alt={file.alt}
                            uploadImmediately
                          />
                        </Box>
                      </div>
                    ))}
                  </Slider>
                </div>
              </Unit>
            )}
          </div>
          {open && (
            <Zoom files={mediaFiles} open={open} onClose={handleZoom} classes={classes} activeIndex={activeIndex} />
          )}
        </Box>
      )}
    </Unit>
  );
};
